import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type ContainerWidth = 'fixed' | 'fluid';

const STORAGE_KEY = 'cimalti-container-width';
const DEFAULT_WIDTH: ContainerWidth = 'fluid';

/** Global page-width switcher.
 *  Mirrors [[theme-service]]'s pattern: persists the preference in
 *  localStorage and exposes the active mode as `data-width` on <body>,
 *  so the single CSS rule that sizes `.main` in `home.component.scss`
 *  picks the right container behavior at runtime. */
@Injectable({ providedIn: 'root' })
export class LayoutService {
  private readonly isBrowser: boolean;
  private width$ = new BehaviorSubject<ContainerWidth>(DEFAULT_WIDTH);
  readonly current$ = this.width$.asObservable();

  constructor(
    @Inject(DOCUMENT) private doc: Document,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    const initial = this.readStored() ?? DEFAULT_WIDTH;
    this.width$.next(initial);
    this.applyToBody(initial);
  }

  get current(): ContainerWidth {
    return this.width$.value;
  }

  set(width: ContainerWidth): void {
    if (width === this.width$.value) return;
    this.width$.next(width);
    this.applyToBody(width);
    this.persist(width);
  }

  toggle(): void {
    this.set(this.current === 'fixed' ? 'fluid' : 'fixed');
  }

  private readStored(): ContainerWidth | null {
    if (!this.isBrowser) return null;
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v === 'fixed' || v === 'fluid') return v;
    } catch { /* noop */ }
    return null;
  }

  private persist(width: ContainerWidth): void {
    if (!this.isBrowser) return;
    try { localStorage.setItem(STORAGE_KEY, width); } catch { /* noop */ }
  }

  private applyToBody(width: ContainerWidth): void {
    const body = this.doc?.body;
    if (!body) return;
    body.setAttribute('data-width', width);
    this.doc.documentElement.setAttribute('data-width', width);
  }
}
